import { BatteryMedium, BookOpen, Camera, Compass, Keyboard, MonitorCheck, Wrench, type LucideIcon } from 'lucide-react';
import { useTilt, v3 } from '@/hooks/useTilt';

/**
 * FreeTools — homepage section surfacing the free interactive tools and the
 * guide. These live as STATIC pages outside the SPA, so links MUST be
 * <a href> (a react-router <Link> would client-route into a 404).
 * Icons are lucide (2026-10-06), not emoji: emoji render differently on every
 * device and as empty boxes where the font lacks them. Each card has a
 * Nano Banana photo and tilts toward the mouse (useTilt, 2026-10-10).
 */
const TOOLS: Array<{ href: string; icon: LucideIcon; image: string; alt: string; tag: string; paid: boolean; title: string; desc: string; cta: string }> = [
  {
    href: '/laptop-quiz/',
    icon: Compass,
    image: 'tool-laptop-picker',
    alt: 'שלושה מחשבים ניידים בגדלים שונים על שולחן',
    tag: 'חינם · 30 שניות',
    paid: false,
    title: 'בוחר המחשב הנייד',
    desc: 'מתלבטים איזה מחשב נייד לקנות? 4 שאלות וקבלו המלצה אישית מלאה, כולל לקנייה להורה / לגיל השלישי.',
    cta: 'לכלי',
  },
  {
    href: '/repair-or-replace/',
    icon: Wrench,
    image: 'tool-repair-or-replace',
    alt: 'מחשב נייד פתוח לתיקון ליד קופסה של מחשב חדש',
    tag: 'חינם · 30 שניות',
    paid: false,
    title: 'לתקן או לקנות חדש?',
    desc: 'המחשב מתקלקל ולא בטוחים אם שווה לתקן? המלצה כנה מטכנאי, כולל הערכת עלות תיקון.',
    cta: 'לבדיקה',
  },
  {
    href: '/laptop-guide-2026/',
    icon: BookOpen,
    image: 'tool-guide',
    alt: 'חוברת מדריך פתוחה ליד מחשב נייד וכוס קפה',
    tag: 'חינם · 67 עמודים',
    paid: false,
    title: 'מדריך קניית מחשב נייד 2026',
    desc: '67 עמודים: דגמים ספציפיים ומחירים, 15 מלכודות בקנייה, איפה לקנות בישראל וזכויות הצרכן שלכם. בלי תשלום.',
    cta: 'להורדה',
  },
];

const CHECKS: Array<{ href: string; icon: LucideIcon; label: string }> = [
  { href: '/tools/keyboard-test/', icon: Keyboard, label: 'בדיקת מקלדת' },
  { href: '/tools/screen-test/', icon: MonitorCheck, label: 'בדיקת מסך' },
  { href: '/tools/camera-mic-test/', icon: Camera, label: 'מצלמה ומיקרופון' },
  { href: '/tools/battery-health/', icon: BatteryMedium, label: 'בריאות הסוללה' },
];

export default function FreeTools() {
  return (
    <section className="cin-wash-blue py-[clamp(64px,8vw,112px)] px-[clamp(20px,5vw,80px)]">
      <div className="mx-auto max-w-[1280px]">
        <div className="cin-reveal mb-12 text-center">
          <div className="cin-label-ed mb-5">כלים חינמיים</div>
          <h2 className="cin-display-sm mx-auto max-w-3xl text-[var(--cin-ink)]">
            לא בטוחים מה לעשות?
            <br />
            <span className="cin-text-gradient-blue">נתחיל מכאן, בחינם.</span>
          </h2>
          <p className="cin-reveal cin-reveal-d1 mx-auto mt-6 max-w-xl text-lg text-[var(--cin-muted)] leading-relaxed">
            כלים קטנים שבניתי כדי שתוכלו להחליט לבד: איזה מחשב לקנות, האם שווה לתקן,
            ומה מצב המקלדת, המסך והסוללה. בחינם.
          </p>
        </div>

        <div className="grid gap-4 md:gap-5 md:grid-cols-3">
          {TOOLS.map((t, i) => (
            <ToolCard key={t.href} tool={t} delay={(i % 3) + 1} />
          ))}
        </div>

        {/* Quick hardware checks (2026-10-10) — static pages too, so plain <a>. */}
        <div className="cin-reveal mt-8 flex flex-col items-center gap-3 text-center">
          <p className="text-[15px] font-semibold text-[var(--cin-ink)]">בדיקות מהירות למחשב, בחינם:</p>
          <div className="flex flex-wrap justify-center gap-2.5">
            {CHECKS.map((c) => {
              const Icon = c.icon;
              return (
                <a
                  key={c.href}
                  href={c.href}
                  className="inline-flex items-center gap-2 rounded-full border border-[var(--cin-line)] bg-white px-4 py-2.5 text-[15px] font-semibold text-[var(--cin-ink)] transition-[border-color,transform] duration-150 ease-[cubic-bezier(0.23,1,0.32,1)] hover:border-[var(--cin-blue)]/50 active:scale-[0.97]"
                >
                  <Icon className="h-4 w-4 text-[var(--cin-blue)]" aria-hidden="true" />
                  {c.label}
                </a>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}

function ToolCard({ tool: t, delay }: { tool: (typeof TOOLS)[number]; delay: number }) {
  const ref = useTilt<HTMLAnchorElement>(5);
  const Icon = t.icon;
  const img = v3(t.image);
  return (
    <a
      ref={ref}
      href={t.href}
      className={`cin-reveal cin-reveal-d${delay} cin-tilt group relative flex flex-col overflow-hidden rounded-2xl border ${
        t.paid
          ? 'border-[var(--cin-amber)]/40 bg-gradient-to-br from-[#fff8ee] to-[#fdf1dd]'
          : 'border-[var(--cin-line)] bg-white hover:border-[var(--cin-blue)]/40'
      }`}
    >
      <div className="relative aspect-[3/2] overflow-hidden bg-[var(--cin-paper-soft)]">
        <img
          src={img.src}
          srcSet={img.srcSet}
          sizes="(min-width: 768px) 400px, 100vw"
          alt={t.alt}
          width={800}
          height={533}
          loading="lazy"
          decoding="async"
          className="h-full w-full object-cover transition-transform duration-500 ease-[cubic-bezier(0.23,1,0.32,1)] group-hover:scale-[1.03]"
        />
        <div className="absolute right-4 bottom-4 flex h-11 w-11 items-center justify-center rounded-xl bg-white/90 text-[var(--cin-blue)] shadow-[0_6px_18px_-8px_rgba(15,23,41,0.45)] backdrop-blur">
          <Icon className="h-5 w-5" strokeWidth={2} aria-hidden="true" />
        </div>
      </div>
      <div className="flex flex-1 flex-col p-6 md:p-7">
        <span
          className={`mb-3 inline-block w-fit rounded-full px-2.5 py-1 text-[11px] font-bold ${
            t.paid ? 'bg-[var(--cin-amber)]/15 text-[var(--cin-amber)]' : 'bg-emerald-500/10 text-emerald-700'
          }`}
        >
          {t.tag}
        </span>
        <h3 className="mb-2 text-lg md:text-xl font-bold leading-snug text-[var(--cin-ink)]">{t.title}</h3>
        <p className="flex-1 text-[15px] leading-relaxed text-[var(--cin-muted)]">{t.desc}</p>
        <span className="mt-5 inline-flex items-center gap-1.5 text-[15px] font-semibold text-[var(--cin-blue)]">
          {t.cta}
          <span aria-hidden="true" className="transition-transform duration-200 ease-[cubic-bezier(0.23,1,0.32,1)] group-hover:-translate-x-1">←</span>
        </span>
      </div>
      <span data-tilt-glare aria-hidden="true" className="pointer-events-none absolute inset-0 rounded-[inherit] opacity-0 transition-opacity duration-300" />
    </a>
  );
}
